import { css, cx } from '@emotion/css';
import { CheckCircle, Clock as ClockIcon, CreditCard, Copy, Check } from 'lucide-react';
import { useEffect, useMemo, useState, type HTMLAttributes } from 'react';
import { useGetUserOrder, useGetOrderPayment } from '../../../checkout/hooks/checkout-api.hooks';
import { Button } from '../../../../shared/ui/button/button';

const wrapper = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--spacing-lg)',
  padding: 'var(--spacing-lg)',
});

const title = css({
  fontSize: 'var(--fs-600)',
  fontWeight: 700,
  color: 'var(--pc-800)',
  margin: 0,
});

const statusPill = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 'var(--fs-300)',
});

const paidPill = css({
  backgroundColor: 'rgba(0, 200, 83, 0.1)',
  color: 'rgb(0, 150, 62)',
});

const pendingPill = css({
  backgroundColor: 'rgba(255, 193, 7, 0.1)',
  color: 'rgb(196, 149, 5)',
});

const info = css({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'var(--spacing-lg)',
  width: '100%',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const card = css({
  backgroundColor: 'rgba(var(--pcrgba-100), 0.2)',
  borderRadius: 'var(--spacing-md)',
  padding: 'var(--spacing-lg)',
});

const pixSection = css({
  width: '100%',
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-md)',
  padding: 'var(--spacing-xl)',
  marginTop: 'var(--spacing-lg)',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  textAlign: 'center',
});

const pixTitle = css({
  fontSize: 'var(--fs-500)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  marginBottom: 'var(--spacing-md)',
});

const qrCodeWrapper = css({
  backgroundColor: 'white',
  borderRadius: 'var(--spacing-md)',
  padding: 'var(--spacing-md)',
  display: 'inline-block',
  marginBottom: 'var(--spacing-md)',
  '& img': {
    display: 'block',
    width: '280px',
    height: '280px',
  },
});

const pixInfo = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--pc-600)',
  marginBottom: 'var(--spacing-md)',
  '& p': {
    marginBottom: 'var(--spacing-xs)',
  },
  '& strong': {
    color: 'var(--pc-700)',
    fontWeight: 600,
  },
});

const copyButton = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  padding: 'var(--spacing-sm) var(--spacing-lg)',
  backgroundColor: 'var(--pc-600)',
  color: 'var(--primary-color)',
  border: 'none',
  borderRadius: 'var(--spacing-sm)',
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: 'var(--pc-700)',
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
});

const actionsSection = css({
  display: 'flex',
  gap: 'var(--spacing-md)',
  justifyContent: 'center',
  marginTop: 'var(--spacing-lg)',
  '@media (max-width: 480px)': {
    flexDirection: 'column',
    width: '100%',
  },
});

const pixTimer = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  padding: 'var(--spacing-xs) var(--spacing-md)',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.3)',
  borderRadius: 'var(--spacing-sm)',
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  marginTop: 'var(--spacing-sm)',
  '& svg': {
    color: 'var(--pc-500)',
  },
});

const pixTimerExpiring = css({
  backgroundColor: 'rgba(220, 53, 69, 0.1)',
  color: '#dc3545',
  animation: 'pulse 1.5s infinite',
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.7 },
    '100%': { opacity: 1 },
  },
});

export interface PaymentSuccessProps extends HTMLAttributes<HTMLDivElement> {
  orderId: string; // orderUuid
  pollIntervalMs?: number; // default 4000ms
}

export function PaymentSuccess({ orderId, pollIntervalMs = 4000, className, ...rest }: PaymentSuccessProps) {
  const [status, setStatus] = useState<string>('pending');
  const [total, setTotal] = useState<number | null>(null);
  const [method, setMethod] = useState<string | null>(null);
  const [paidAt, setPaidAt] = useState<string | null>(null);
  const [copiedPix, setCopiedPix] = useState(false);
  const [pixTimeRemaining, setPixTimeRemaining] = useState<string | null>(null);
  const [isPixExpiring, setIsPixExpiring] = useState(false);

  const isPaid = useMemo(() => {
    const s = String(status || '').toLowerCase();
    return s === 'paid' || s === 'approved' || s === 'success';
  }, [status]);

  // Fetch order by UUID with polling until paid/approved
  const { data: orderData } = useGetUserOrder(orderId, !!orderId, isPaid ? false : Math.max(2000, pollIntervalMs));
  
  // Fetch payment details (includes PIX QR code if applicable)
  const { data: paymentData } = useGetOrderPayment(orderId, !!orderId, isPaid ? false : Math.max(2000, pollIntervalMs));

  useEffect(() => {
    const ord: any = orderData?.order || orderData;
    if (!ord) return;
    const invoice: any = ord?.invoice || ord?.payment || ord?.lastPayment || null;
    const st = invoice?.paymentStatus || invoice?.status || ord?.status || 'pending';
    const amt = Number(ord?.totalAmount || invoice?.transactionAmount || invoice?.value || 0);
    const mth = invoice?.paymentMethod || invoice?.method || ord?.paymentMethod || null;
    const paid = invoice?.paidAt || invoice?.dateApproved || ord?.paidAt || null;
    setStatus(st);
    setTotal(Number.isFinite(amt) ? amt : null);
    setMethod(mth);
    setPaidAt(paid);
  }, [orderData]);

  const payment = paymentData?.payment;
  
  // Check if it's a PIX payment with QR code
  const isPixPayment = method?.toLowerCase() === 'pix';
  const pixQrCode = 
    payment?.point_of_interaction?.transaction_data?.qr_code_base64 ||
    payment?.qrCodeBase64 ||
    payment?.pix?.qrCodeBase64;
  
  const pixCode = 
    payment?.point_of_interaction?.transaction_data?.qr_code ||
    payment?.qrCode ||
    payment?.pix?.qrCode;

  // Get PIX expiration date
  const pixExpirationDate = 
    payment?.date_of_expiration ||
    payment?.dateOfExpiration ||
    payment?.expirationDate ||
    payment?.pix?.expirationDate;

  // Countdown timer for PIX expiration
  useEffect(() => {
    if (!isPixPayment || !pixExpirationDate || isPaid) {
      setPixTimeRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const expiration = new Date(pixExpirationDate).getTime();
      const timeLeft = expiration - now;

      if (timeLeft <= 0) {
        setPixTimeRemaining('Expirado');
        setIsPixExpiring(true);
        return;
      }

      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      // Mark as expiring if less than 5 minutes
      setIsPixExpiring(timeLeft < 5 * 60 * 1000);

      if (hours > 0) {
        setPixTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setPixTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setPixTimeRemaining(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [isPixPayment, pixExpirationDate, isPaid]);

  const handleCopyPixCode = () => {
    if (pixCode) {
      navigator.clipboard.writeText(pixCode);
      setCopiedPix(true);
      setTimeout(() => setCopiedPix(false), 2000);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <div className={cx(wrapper, className)} {...rest}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {isPaid ? (
          <CheckCircle color="#00C853" size={28} />
        ) : (
          <ClockIcon color="#C19505" size={28} />
        )}
        <h2 className={title}>
          {isPaid 
            ? 'Pedido concluído' 
            : isPixPayment 
            ? 'Aguardando pagamento PIX' 
            : 'Aguardando confirmação do pagamento'}
        </h2>
      </div>

      <div className={cx(statusPill, isPaid ? paidPill : pendingPill)}>
        {isPaid ? 'Pago' : 'Pendente'}
      </div>

      {/* PIX QR Code Section - only show if PIX payment and not paid yet */}
      {isPixPayment && !isPaid && pixQrCode && (
        <div className={pixSection}>
          <h3 className={pixTitle}>Escaneie o QR Code para pagar</h3>
          <div className={qrCodeWrapper}>
            <img
              src={`data:image/png;base64,${pixQrCode}`}
              alt="QR Code PIX"
            />
          </div>
          <div className={pixInfo}>
            <p>
              Valor: <strong>{total != null ? formatCurrency(total) : '—'}</strong>
            </p>
            {pixTimeRemaining && (
              <div className={cx(pixTimer, isPixExpiring && pixTimerExpiring)}>
                <ClockIcon size={16} />
                <span>Tempo restante: {pixTimeRemaining}</span>
              </div>
            )}
          </div>
          <button 
            className={copyButton} 
            onClick={handleCopyPixCode}
            disabled={!pixCode}
          >
            {copiedPix ? <Check size={18} /> : <Copy size={18} />}
            {copiedPix ? 'Código Copiado!' : 'Copiar Código PIX'}
          </button>
        </div>
      )}

      <div className={info}>
        <div className={card}>
          <strong>Status</strong>
          <div style={{ marginTop: 6 }}>{String(status || 'pending')}</div>
        </div>
        <div className={card}>
          <strong>Total</strong>
          <div style={{ marginTop: 6 }}>
            {total != null ? formatCurrency(total) : '—'}
          </div>
        </div>
        <div className={card}>
          <strong>Método</strong>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <CreditCard size={16} /> {method || '—'}
          </div>
        </div>
        <div className={card}>
          <strong>Pago em</strong>
          <div style={{ marginTop: 6 }}>{paidAt ? new Date(paidAt).toLocaleString('pt-BR') : '—'}</div>
        </div>
      </div>

      {/* Action Buttons */}
      {isPaid && (
        <div className={actionsSection}>
          <Button onClick={() => window.location.href = '/perfil/ingressos'}>
            Ver Meus Ingressos
          </Button>
        </div>
      )}
    </div>
  );
}

export default PaymentSuccess;
